import React, { useMemo } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { ChartDataset } from 'chart.js'

import Text from '../../../ui/Text'
import { ConsumptionWidgetLayout } from '../../../lib/consumption-widget-layout'
import {
    MeasurementDeviceExtendedGetModel,
    useMeasurementDevicesGetMeasurementsForLatest24HoursQuery,
} from '../../redux/api/web-api'
import { LineChart } from '../LineChart'
import { SNoAvailableDataAlert } from '../../../lib/load-balancer-statistics'
import useText from '../../hooks/useText'

export type Props = {
    device?: MeasurementDeviceExtendedGetModel
    className?: ClassName
    style?: React.CSSProperties
}

export function MeasurementDeviceStatistics({ device, className, style }: Props): React.ReactElement | null {
    const text = useText()
    const { data: measurementData = [], isFetching: measurementDataFetching } =
        useMeasurementDevicesGetMeasurementsForLatest24HoursQuery(
            {
                id: device?.id ?? 0,
            },
            { skip: !device?.id }
        )

    const hasData = measurementData && measurementData.length > 0

    const chartData = useMemo(() => {
        const current1 = []
        const current2 = []
        const current3 = []

        if (hasData) {
            for (const item of measurementData) {
                const x = new Date(item.timestamp!).getTime()

                current1.push({ y: Number(item.current1?.toFixed(3)) ?? NaN, x })
                current2.push({ y: Number(item.current2?.toFixed(3)) ?? NaN, x })
                current3.push({ y: Number(item.current3?.toFixed(3)) ?? NaN, x })
            }
        }

        const datasets: ChartDataset<'line'>[] = [
            {
                label: `L1 ${text('lblCurrent')}`,
                data: current1,
            },
            {
                label: `L2 ${text('lblCurrent')}`,
                data: current2,
            },
            {
                label: `L3 ${text('lblCurrent')}`,
                data: current3,
            },
        ]

        return {
            datasets,
        }
    }, [hasData, measurementData])

    return (
        <ConsumptionWidgetLayout
            className={cn(className)}
            style={style}
            title={text('lblMDStatistics')}
            dataTestId="measurementDevice"
            loadingStatus={measurementDataFetching}
        >
            {hasData ? (
                <>
                    <LineChart yAxisLabel={text('lblCurrentAxis')} xAxisLabel={text('lblTime')} data={chartData} />
                </>
            ) : (
                <SNoAvailableDataAlert data-testid="no-data-measurementDevice">
                    <Text code="msgNoAvailableData" />
                </SNoAvailableDataAlert>
            )}
        </ConsumptionWidgetLayout>
    )
}
